'use client';

import { IGroupInfo } from '@/api/types/product';
import { getHtmlText } from '@/utils/goods-list-utils';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useState } from 'react';
import styles from './FaqPart.module.scss';

interface FaqPartProps {
  groupInfo: IGroupInfo;
  isWeb?: boolean;
  defaultExpandAll?: boolean;
  defaultQuestionLevel?: number;
}

interface FaqItem {
  question: string;
  answer?: string;
  level: number;
  isShow?: boolean;
}

export default function FaqPart({
  groupInfo,
  isWeb = false,
  defaultExpandAll = false,
  defaultQuestionLevel,
}: FaqPartProps) {
  const group = groupInfo as any; // 临时类型断言
  const faqList = group?.faq_list || [];

  // 为每个FAQ项添加展开状态（首条默认展开；length 为 0 时不可用 .with(0)，需单独处理）
  const [faqStates, setFaqStates] = useState<boolean[]>(() => {
    const len = faqList.length;
    if (len === 0) return [];
    if (defaultExpandAll) return new Array(len).fill(true);
    return new Array(len).fill(false).with(0, true);
  });

  // 处理问题文本
  const getQuestionText = (item: FaqItem) => {
    const { question, level } = item;
    const headingLevel = level || defaultQuestionLevel;
    const text = `<h${headingLevel}>${question}</h${headingLevel}>`;
    return getHtmlText(text);
  };

  // 切换FAQ展开状态
  const toggleFaq = (index: number) => {
    if (!faqList[index]?.answer) return;

    setFaqStates((prev) => {
      const newStates = [...prev];
      newStates[index] = !newStates[index];
      return newStates;
    });
  };

  if (!group?.h_one && (!faqList || faqList.length === 0)) {
    return null;
  }

  return (
    <div
      className={`content mx-auto bg-[rgba(70,58,232,.1)] ${isWeb ? '' : 'mt-[24px] pb-[30px] px-14'}`}
    >
      <div className={`faq-wrap ${isWeb ? 'pb-[72px] max-w-[1064px] px-20 mx-auto' : ''}`}>
        {/* 主标题 */}
        {group?.h_one && (
          <h1
            className={`h-one  font-bb text-center border-b border-[#d4d4d4] ${isWeb ? 'pt-[72px] pb-36 text-32' : 'pt-32 pb-[21px] text-[20px]'}`}
            dangerouslySetInnerHTML={{ __html: getHtmlText(group.h_one) }}
          />
        )}

        {/* FAQ列表 */}
        {faqList && faqList.length > 0 && (
          <ul>
            {faqList.map((item: FaqItem, index: number) => (
              <li key={item.question} className="border-b border-[#d4d4d4]">
                {/* 问题 */}
                <div
                  className={`
                    question flex justify-between items-start font-bold text-14 py-16 cursor-pointer
                    ${faqStates[index] && item.answer ? 'pb-10' : ''} ${isWeb ? 'py-24' : ''}
                  `}
                  onClick={() => toggleFaq(index)}
                >
                  <div
                    className={`${styles.questionWrapper} flex-1 text-left`}
                    dangerouslySetInnerHTML={{ __html: getQuestionText(item) }}
                  />
                  {item.answer && (
                    <CollapseArrowDownOutlined
                      className={`text-16 text-primary-text transition-transform duration-300 ${
                        faqStates[index] ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </div>

                {/* 答案 */}
                {item.answer && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      faqStates[index] ? 'max-h-none' : 'max-h-0'
                    }`}
                  >
                    <div
                      className={`${styles.answer} text-left text-primary-text text-[12px] leading-[18px] pb-[16px]`}
                      dangerouslySetInnerHTML={{ __html: getHtmlText(item.answer) }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
